import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Container, Row, Col, ListGroup, Badge } from 'react-bootstrap';
import NearbyStores from './NearbyStores';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../common_components/ConfirmationModal';

const UserFavoriteStores = () => {
    const [stores, setStores] = useState([]);
    const [nextUrl, setNextUrl] = useState(null); // State to track the next URL for pagination
    const [loading, setLoading] = useState(false); // State to manage loading indicator
    const token = localStorage.getItem('token');
    const listGroupRef = useRef(null); // Ref to track the ListGroup element

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [storeToRemove, setStoreToRemove] = useState(null);

    const pageSize = 10;

    // Memoized fetch function for favorite stores
    const fetchUserFavoriteStores = useCallback(async (nextUrl = null) => {
        setLoading(true); // Set loading to true when fetching starts
        const params = {
            page_size: pageSize
        };

        const url = nextUrl || `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-stores/`;
        try {
            const response = await axios.get(url, {
                params,
                headers: { 'Authorization': `Token ${token}` }
            });

            if (nextUrl) {
                setStores(prevStores => [...prevStores, ...response.data.results]);
            } else {
                setStores(response.data.results);
            }
            setNextUrl(response.data.next);
        } catch (error) {
            console.error("Error fetching user favorite stores:", error);
            toast.error("Error fetching user favorite stores!");
        } finally {
            setLoading(false); // Set loading to false when fetching completes
        }
    }, [token, pageSize]);

    useEffect(() => {
        setStores([]); // Reset stores list on page load
        setNextUrl(null); // Reset pagination URL on page load
        fetchUserFavoriteStores();
    }, [fetchUserFavoriteStores]);

    useEffect(() => {
        const handleScroll = () => {
            if (listGroupRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = listGroupRef.current;

                if (scrollTop + clientHeight >= scrollHeight - 20 && nextUrl && !loading) {
                    fetchUserFavoriteStores(nextUrl);
                }
            }
        };

        const listGroupElement = listGroupRef.current;
        if (listGroupElement) {
            listGroupElement.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (listGroupElement) {
                listGroupElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [nextUrl, loading, fetchUserFavoriteStores]);

    const handleRemoveFavorite = async (storeId) => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-stores/${storeId}/`, {
                headers: { 'Authorization': `Token ${token}` }
            });

            setStoreToRemove(null);
            setShowConfirmationModal(false);
            setStores(prevStores => prevStores.filter(store => store.id !== storeId)); // Update the state to remove the store
            toast.success("Store removed from favorites successfully!");
        } catch (error) {
            console.error("Error removing favorite store:", error);
            toast.error("Error removing favorite store!");
        }
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                <Row>
                    <ListGroup ref={listGroupRef} className="hidden-scrollbar" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {stores.length === 0 && !loading ? (
                            <ListGroup.Item className="text-center text-muted border-0">
                                No favorite stores yet. Add some from nearby stores below!
                            </ListGroup.Item>
                        ) : (
                            stores.map((store) => (
                                <ListGroup.Item key={store.id} className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <Badge bg="none" style={{ backgroundColor: '#aec15f', color: 'white', marginRight: '7px' }}>
                                            {store.store_details.store_brand.website ?
                                                <a href={store.store_details.store_brand.website} target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>
                                                    {store.store_details.store_brand.name}
                                                </a> :
                                                store.store_details.store_brand.name
                                            }
                                        </Badge>
                                        {store.store_name || store.store_details.name}
                                    </div>
                                    <i className="bi bi-trash"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                            setStoreToRemove(store.id);
                                            setShowConfirmationModal(true);
                                            e.stopPropagation();
                                        }}></i>
                                </ListGroup.Item>
                            ))
                        )}
                    </ListGroup>
                    </Row>
                    <Row className="mt-4">
                        <NearbyStores onStoreAdded={() => {
                            setStores([]);
                            setNextUrl(null);
                            fetchUserFavoriteStores();
                        }} />
                    </Row>
                </Col>
            </Row>

            <ConfirmationModal
                show={showConfirmationModal}
                onHide={() => setShowConfirmationModal(false)}
                onConfirm={() => handleRemoveFavorite(storeToRemove)}
                title="Remove Store from Favorites"
                body="Are you sure you want to remove this store from your favorites?"
            />
        </Container>
    );
};

export default UserFavoriteStores;
